import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const STORAGE_KEY = "active_branch_id";

const BranchCtx = createContext(null);

export function BranchProvider({ children }) {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [activeBranchId, setActiveBranchIdState] = useState(null);
  const [activeBranchName, setActiveBranchName] = useState("");
  const [ready, setReady] = useState(false);

  const isMaster = Boolean(
    user?.is_master || (user?.role === "admin" && (user?.branch_id === null || user?.branch_id === undefined))
  );

  useEffect(() => {
    if (!user) {
      setReady(true);
      return;
    }

    const init = async () => {
      let list = user.branches || [];
      if (isMaster && !list.length) {
        try {
          const { data } = await api.get("/auth/me");
          list = data.branches || [];
        } catch {
          list = [];
        }
      }
      setBranches(list);

      if (!isMaster && user.branch_id) {
        let branch = user.branch;
        if (!branch) {
          try {
            const { data } = await api.get("/auth/me");
            branch = data.branch;
          } catch {
            branch = null;
          }
        }
        if (branch) {
          setBranches([branch]);
          setActiveBranchName(branch.name || "");
        }
        setActiveBranchIdState(user.branch_id);
        localStorage.setItem(STORAGE_KEY, user.branch_id);
        setReady(true);
        return;
      }

      const stored = localStorage.getItem(STORAGE_KEY);
      const tepe = list.find((b) => b.slug === "tepeapulco");
      const fallback = tepe?.id || list[0]?.id || null;
      const active = stored && list.some((b) => b.id === stored) ? stored : fallback;
      if (active) {
        setActiveBranchIdState(active);
        localStorage.setItem(STORAGE_KEY, active);
        const selected = list.find((b) => b.id === active);
        setActiveBranchName(selected?.name || "");
      }
      setReady(true);
    };

    init();
  }, [user, isMaster]);

  const setActiveBranch = useCallback((id) => {
    setActiveBranchIdState(id);
    localStorage.setItem(STORAGE_KEY, id);
    const selected = branches.find((b) => b.id === id);
    setActiveBranchName(selected?.name || "");
    window.dispatchEvent(new CustomEvent("branch-changed", { detail: { branchId: id } }));
  }, [branches]);

  const activeBranch = branches.find((b) => b.id === activeBranchId) || null;

  return (
    <BranchCtx.Provider
      value={{
        branches,
        activeBranchId,
        activeBranch,
        activeBranchName,
        isMaster,
        setActiveBranch,
        ready,
      }}
    >
      {children}
    </BranchCtx.Provider>
  );
}

export const useBranch = () => useContext(BranchCtx);
